import axios, { AxiosInstance, AxiosError } from "axios";
import {
  ApiResponse,
  AuthResponse,
  PaginatedResponse,
  User,
  LoginCredentials,
  RegisterData,
  Salesperson,
  CreateSalespersonData,
  CallAnalysis,
  CallFilters,
  CompanyAnalysisSettings,
  UpdateAnalysisSettingsData,
  AnalysisParameter,
  AnalysisParametersResponse,
  AddAnalysisParameterData,
  UpdateAnalysisParameterData,
  PaginatedSalespeopleResponse,
  CompanyStats,
  SalespersonStats,
  SalespersonLoginInfo,
} from "../types/api";

// New interfaces for batch upload
export interface BatchUploadResponse {
  success: boolean;
  message: string;
  data: {
    batchId: string;
    totalFiles: number;
    results: Array<{
      filename: string;
      callId?: string;
      status: "success" | "error";
      error?: string;
    }>;
  };
  timestamp: string;
}

export interface FileUploadResult {
  filename: string;
  callId?: string;
  status: "success" | "error";
  error?: string;
}

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        const isAuthEndpoint =
          error.config?.url?.includes("/auth/login") ||
          error.config?.url?.includes("/auth/register") ||
          error.config?.url?.includes("/auth/forgot-password") ||
          error.config?.url?.includes("/auth/reset-password") ||
          error.config?.url?.includes("/auth/profile");

        if (error.response?.status === 401 && !isAuthEndpoint) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          window.location.href = "/login";
        }
        return Promise.reject(error);
      },
    );
  }

  // Auth endpoints
  async login(
    credentials: LoginCredentials,
  ): Promise<ApiResponse<AuthResponse>> {
    const response = await this.api.post("/auth/login", credentials);
    return response.data;
  }

  async register(data: RegisterData): Promise<ApiResponse<AuthResponse>> {
    const response = await this.api.post("/auth/register", data);
    return response.data;
  }

  async getProfile(): Promise<ApiResponse<User>> {
    const response = await this.api.get("/auth/profile");
    return response.data;
  }

  async changePassword(data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<ApiResponse> {
    const response = await this.api.put("/auth/change-password", data);
    return response.data;
  }

  // Forgot password - request reset email
  async forgotPassword(email: string): Promise<ApiResponse<void>> {
    const response = await this.api.post("/auth/forgot-password", { email });
    return response.data;
  }

  // Verify reset token
  async verifyResetToken(
    token: string,
  ): Promise<ApiResponse<{ email: string }>> {
    const response = await this.api.get(`/auth/verify-reset-token/${token}`);
    return response.data;
  }

  // Reset password with token
  async resetPassword(
    token: string,
    newPassword: string,
  ): Promise<ApiResponse<void>> {
    const response = await this.api.post("/auth/reset-password", {
      token,
      newPassword,
    });
    return response.data;
  }

  // Salespeople endpoints
  async getSalespeople(params?: any): Promise<PaginatedSalespeopleResponse> {
    const response = await this.api.get("/salespeople", { params });
    return response.data;
  }

  async getSalespeopleList(): Promise<ApiResponse<Salesperson[]>> {
    const response = await this.api.get("/salespeople/list");
    return response.data;
  }

  async getSalesperson(id: string): Promise<ApiResponse<Salesperson>> {
    const response = await this.api.get(`/salespeople/${id}`);
    return response.data;
  }

  async createSalesperson(
    data: CreateSalespersonData,
  ): Promise<ApiResponse<Salesperson>> {
    const response = await this.api.post("/salespeople", data);
    return response.data;
  }

  async updateSalesperson(
    id: string,
    data: Partial<CreateSalespersonData>,
  ): Promise<ApiResponse> {
    const response = await this.api.put(`/salespeople/${id}`, data);
    return response.data;
  }

  async deleteSalesperson(id: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/salespeople/${id}`);
    return response.data;
  }

  async getSalespersonStats(
    id: string,
  ): Promise<ApiResponse<SalespersonStats>> {
    const response = await this.api.get(`/salespeople/${id}/stats`);
    return response.data;
  }

  async getSalespersonCalls(
    id: string,
    params?: CallFilters,
  ): Promise<
    ApiResponse<{
      salesperson: {
        id: string;
        firstName: string;
        lastName: string;
        fullName: string;
      };
      calls: CallAnalysis[];
      totalCount: number;
      currentPage: number;
      totalPages: number;
    }>
  > {
    const response = await this.api.get(`/salespeople/${id}/calls`, { params });
    return response.data;
  }

  async searchSalespeople(params: {
    q: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedSalespeopleResponse> {
    const response = await this.api.get("/salespeople/search", { params });
    return response.data;
  }

  async createSalespersonLogin(
    salespersonId: string,
    data: { email: string; password: string; userType?: "manager" | "salesperson" },
  ): Promise<ApiResponse<any>> {
    const response = await this.api.post(
      `/salespeople/${salespersonId}/login`,
      data,
    );
    return response.data;
  }

  async updateSalespersonLogin(
    salespersonId: string,
    data: { email?: string; password?: string; userType?: "manager" | "salesperson" },
  ): Promise<ApiResponse<any>> {
    const response = await this.api.put(
      `/salespeople/${salespersonId}/login`,
      data,
    );
    return response.data;
  }

  async deleteSalespersonLogin(
    salespersonId: string,
  ): Promise<ApiResponse<any>> {
    const response = await this.api.delete(
      `/salespeople/${salespersonId}/login`,
    );
    return response.data;
  }

  async getSalespersonLoginInfo(
    salespersonId: string,
  ): Promise<ApiResponse<SalespersonLoginInfo>> {
    const response = await this.api.get(`/salespeople/${salespersonId}/login`);
    return response.data;
  }

  // Enhanced Call Analysis endpoints with batch support
  async uploadCall(
    formData: FormData,
  ): Promise<ApiResponse<{ callId: string; status: string }>> {
    const response = await this.api.post("/calls/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  }

  // New batch upload method
  async uploadCallsBatch(
    formData: FormData,
    onProgress?: (progress: number) => void,
  ): Promise<BatchUploadResponse> {
    const response = await this.api.post("/calls/upload/batch", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onProgress(progress);
        }
      },
    });
    return response.data;
  }

  // Enhanced single upload with better progress tracking
  async uploadCallWithProgress(
    formData: FormData,
    onProgress?: (progress: number) => void,
  ): Promise<ApiResponse<{ callId: string; status: string }>> {
    const response = await this.api.post("/calls/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onProgress(progress);
        }
      },
      timeout: 300000, // 5 minutes timeout
    });
    return response.data;
  }

  // Validate files before upload
  async validateAudioFiles(files: File[]): Promise<
    ApiResponse<{
      validFiles: Array<{ file: File; valid: boolean; error?: string }>;
      totalValid: number;
      totalInvalid: number;
    }>
  > {
    const formData = new FormData();

    files.forEach((file, index) => {
      formData.append(`files`, file);
    });

    const response = await this.api.post("/calls/validate", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  }

  async getCalls(
    params?: CallFilters,
  ): Promise<PaginatedResponse<CallAnalysis>> {
    const response = await this.api.get("/calls", { params });
    return response.data;
  }

  async getCall(id: string): Promise<ApiResponse<CallAnalysis>> {
    const response = await this.api.get(`/calls/${id}`);
    return response.data;
  }

  async getCallStatus(
    id: string,
  ): Promise<ApiResponse<{ id: string; status: string; progress: number }>> {
    const response = await this.api.get(`/calls/${id}/status`);
    return response.data;
  }

  async getCallAudioUrl(
    id: string,
  ): Promise<ApiResponse<{ url: string; expiresIn: number }>> {
    const response = await this.api.get(`/calls/${id}/audio-url`);
    return response.data;
  }

  // Get batch upload status
  async getBatchStatus(batchId: string): Promise<
    ApiResponse<{
      batchId: string;
      totalFiles: number;
      completedFiles: number;
      failedFiles: number;
      status: "processing" | "completed" | "failed";
      files: Array<{
        filename: string;
        callId?: string;
        status: "pending" | "processing" | "completed" | "failed";
        progress: number;
        error?: string;
      }>;
    }>
  > {
    const response = await this.api.get(`/calls/batch/${batchId}/status`);
    return response.data;
  }

  async updateManagerNotes(
    id: string,
    managerNotes: string,
  ): Promise<ApiResponse> {
    const response = await this.api.put(`/calls/${id}/notes`, { managerNotes });
    return response.data;
  }

  async deleteCall(id: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/calls/${id}`);
    return response.data;
  }

  // Bulk delete calls
  async deleteCallsBatch(callIds: string[]): Promise<
    ApiResponse<{
      deleted: number;
      failed: number;
      errors?: Array<{ callId: string; error: string }>;
    }>
  > {
    const response = await this.api.post("/calls/delete/batch", { callIds });
    return response.data;
  }

  async getAnalysisSettings(): Promise<ApiResponse<CompanyAnalysisSettings>> {
    const response = await this.api.get("/companies/analysis-settings");
    return response.data;
  }

  async updateAnalysisSettings(
    data: UpdateAnalysisSettingsData,
  ): Promise<ApiResponse<CompanyAnalysisSettings>> {
    const response = await this.api.put("/companies/analysis-settings", data);
    return response.data;
  }

  // Analysis Parameters endpoints
  async getAnalysisParameters(): Promise<
    ApiResponse<AnalysisParametersResponse>
  > {
    const response = await this.api.get("/companies/parameters");
    return response.data;
  }

  async addAnalysisParameter(
    data: AddAnalysisParameterData,
  ): Promise<ApiResponse<AnalysisParameter>> {
    const response = await this.api.post("/companies/parameters", data);
    return response.data;
  }

  async updateAnalysisParameter(
    parameterId: string,
    data: UpdateAnalysisParameterData,
  ): Promise<ApiResponse<AnalysisParameter>> {
    const response = await this.api.put(
      `/companies/parameters/${parameterId}`,
      data,
    );
    return response.data;
  }

  async deleteAnalysisParameter(parameterId: string): Promise<ApiResponse> {
    const response = await this.api.delete(
      `/companies/parameters/${parameterId}`,
    );
    return response.data;
  }

  async toggleParameterStatus(
    parameterId: string,
    isEnabled: boolean,
  ): Promise<ApiResponse<AnalysisParameter>> {
    const response = await this.api.patch(
      `/companies/parameters/${parameterId}/toggle`,
      { isEnabled },
    );
    return response.data;
  }

  async getCompanyStats(): Promise<ApiResponse<CompanyStats>> {
    const response = await this.api.get("/companies/stats");
    return response.data;
  }

  async getCallMonthlyBreakdown(): Promise<
    ApiResponse<{
      months: string[];
      highPerforming: number[];
      average: number[];
      needsImprovement: number[];
      totalCalls: number;
      trend: number | null;
    }>
  > {
    const response = await this.api.get("/calls/trends/monthly-breakdown");
    return response.data;
  }

  async getCallMonthlyTrends(): Promise<
    ApiResponse<{
      months: string[];
      avgGrades: (number | null)[];
      callCounts: number[];
      totalCalls: number;
      trend: number | null;
      trendPct: number | null;
    }>
  > {
    const response = await this.api.get("/calls/trends/monthly");
    return response.data;
  }

  async getSalespersonMonthlyTrends(id: string): Promise<
    ApiResponse<{
      months: string[];
      avgGrades: (number | null)[];
      callCounts: number[];
      totalCalls: number;
      trend: number | null;
      trendPct: number | null;
    }>
  > {
    const response = await this.api.get(
      `/salespeople/${id}/trends/monthly`,
    );
    return response.data;
  }

  async getSalespersonMonthlyBreakdown(id: string): Promise<
    ApiResponse<{
      months: string[];
      highPerforming: number[];
      average: number[];
      needsImprovement: number[];
      totalCalls: number;
      trend: number | null;
    }>
  > {
    const response = await this.api.get(
      `/salespeople/${id}/trends/monthly-breakdown`,
    );
    return response.data;
  }

  // Users endpoints
  async getUsers(params?: any): Promise<PaginatedResponse<User>> {
    const response = await this.api.get("/users", { params });
    return response.data;
  }

  async getUser(id: string): Promise<ApiResponse<User>> {
    const response = await this.api.get(`/users/${id}`);
    return response.data;
  }

  async updateUser(id: string, data: Partial<User>): Promise<ApiResponse> {
    const response = await this.api.put(`/users/${id}`, data);
    return response.data;
  }

  async deleteUser(id: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/users/${id}`);
    return response.data;
  }

  // Enhanced polling method with better error handling and timeout
  async pollCallStatus(
    callId: string,
    onStatusChange?: (status: string, progress: number) => void,
    maxAttempts: number = 60,
    intervalMs: number = 5000,
  ): Promise<CallAnalysis> {
    let attempts = 0;

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const statusResponse = await this.getCallStatus(callId);

          if (!statusResponse.success || !statusResponse.data) {
            throw new Error("Invalid status response");
          }

          const { status, progress } = statusResponse.data;

          if (onStatusChange) {
            onStatusChange(status, progress);
          }

          if (status === "completed") {
            try {
              const callResponse = await this.getCall(callId);
              if (callResponse.success && callResponse.data) {
                resolve(callResponse.data);
                return;
              } else {
                throw new Error("Failed to retrieve completed call data");
              }
            } catch (error) {
              reject(
                new Error(
                  `Call completed but failed to retrieve data: ${
                    error instanceof Error ? error.message : "Unknown error"
                  }`,
                ),
              );
              return;
            }
          }

          if (status === "failed") {
            reject(new Error("Call analysis failed"));
            return;
          }

          attempts++;
          if (attempts >= maxAttempts) {
            reject(new Error(`Polling timeout after ${maxAttempts} attempts`));
            return;
          }

          setTimeout(poll, intervalMs);
        } catch (error) {
          console.error("Polling error:", error);
          attempts++;

          if (attempts >= maxAttempts) {
            reject(
              new Error(
                `Polling failed after ${maxAttempts} attempts: ${
                  error instanceof Error ? error.message : "Unknown error"
                }`,
              ),
            );
            return;
          }

          // Retry with exponential backoff
          const backoffDelay = Math.min(
            intervalMs * Math.pow(1.5, attempts - 1),
            30000,
          );
          setTimeout(poll, backoffDelay);
        }
      };

      poll();
    });
  }

  // Batch polling for multiple calls
  async pollBatchStatus(
    batchId: string,
    onStatusChange?: (batchStatus: any) => void,
    maxAttempts: number = 120,
    intervalMs: number = 3000,
  ): Promise<any> {
    let attempts = 0;

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const statusResponse = await this.getBatchStatus(batchId);

          if (!statusResponse.success || !statusResponse.data) {
            throw new Error("Invalid batch status response");
          }

          const batchStatus = statusResponse.data;

          if (onStatusChange) {
            onStatusChange(batchStatus);
          }

          if (
            batchStatus.status === "completed" ||
            batchStatus.status === "failed"
          ) {
            resolve(batchStatus);
            return;
          }

          attempts++;
          if (attempts >= maxAttempts) {
            reject(
              new Error(`Batch polling timeout after ${maxAttempts} attempts`),
            );
            return;
          }

          setTimeout(poll, intervalMs);
        } catch (error) {
          console.error("Batch polling error:", error);
          attempts++;

          if (attempts >= maxAttempts) {
            reject(
              new Error(
                `Batch polling failed: ${
                  error instanceof Error ? error.message : "Unknown error"
                }`,
              ),
            );
            return;
          }

          setTimeout(poll, intervalMs * 1.5);
        }
      };

      poll();
    });
  }

  // Utility method to check if the API is healthy
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.api.get("/health");
      return response.status === 200;
    } catch (error) {
      console.error("Health check failed:", error);
      return false;
    }
  }

  // Get upload limits and configuration
  async getUploadConfig(): Promise<
    ApiResponse<{
      maxFileSize: number;
      maxBatchSize: number;
      supportedFormats: string[];
      maxConcurrentUploads: number;
    }>
  > {
    const response = await this.api.get("/calls/upload/config");
    return response.data;
  }

  // Get company usage statistics
  async getCompanyUsageStats(): Promise<
    ApiResponse<{
      monthly_minute_limit: number;
      seconds_used_current_period: number;
      current_period_start: string;
      current_period_end: string;
      remaining_seconds: number;
      usage_percentage: number;
      calls_this_period: number;
    }>
  > {
    const response = await this.api.get("/companies/usage-stats");
    return response.data;
  }

  // Get company limits
  async getCompanyLimits(): Promise<
    ApiResponse<{
      max_general_instructions_chars: number;
      max_red_flags_instructions_chars: number;
      max_parameter_instructions_chars: number;
      max_parameters_per_scenario: number;
    }>
  > {
    const response = await this.api.get("/companies/limits");
    return response.data;
  }

  // Plans endpoints
  async getPlans(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get("/plans");
    return response.data;
  }

  async createCheckoutSession(data: {
    planId: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    companyName: string;
    isTrial?: boolean;
  }): Promise<ApiResponse<{ sessionId: string; url: string }>> {
    const response = await this.api.post("/checkout/create-session", data);
    return response.data;
  }

  async verifyCheckoutSession(
    sessionId: string,
  ): Promise<ApiResponse<{ status: string; customerEmail: string }>> {
    const response = await this.api.get(`/checkout/verify/${sessionId}`);
    return response.data;
  }

  async checkAccountReady(
    sessionId: string,
  ): Promise<ApiResponse<{ ready: boolean }>> {
    const response = await this.api.get(`/checkout/account-ready/${sessionId}`);
    return response.data;
  }

  async getSubscriptionStatus(): Promise<
    ApiResponse<{
      status: "active" | "trialing" | "past_due" | "canceled";
      paymentFailedAt: string | null;
      gracePeriodEnds: string | null;
      gracePeriodActive: boolean;
      currentPeriodEnd: string;
      cancelAtPeriodEnd: boolean;
      planName: string | null;
      planPriceCents: number | null;
    }>
  > {
    const response = await this.api.get("/companies/subscription-status");
    return response.data;
  }

  async createCustomerPortalSession(): Promise<ApiResponse<{ url: string }>> {
    const response = await this.api.post("/checkout/create-portal-session");
    return response.data;
  }
}

export const apiService = new ApiService();
